import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { carouselSlides } from '../data/carousel';
import { useMediaQuery } from '../hooks/useMediaQuery';
const total = carouselSlides.length;
const extendedSlides = [...carouselSlides, ...carouselSlides, ...carouselSlides];
const ease = [0.23, 1, 0.32, 1] as const;
export function ContentCarousel() {
  const [index, setIndex] = useState(total);
  const [instant, setInstant] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const reduceMotion = useReducedMotion();
  const slidePct = isDesktop ? 100 / 3 : 100;
  const offset = -(index * slidePct) + (isDesktop ? slidePct : 0);
  const activeReal = (index % total + total) % total;
  const go = (direction: number) => {
    setInstant(false);
    setIndex(current => {
      const next = current + direction;
      if (next < 0 || next >= total * 3) return current;
      return next;
    });
  };
  const goTo = (realIndex: number) => {
    setInstant(false);
    setIndex(total + realIndex);
  };
  const handleAnimationComplete = () => {
    if (index >= total * 2) {
      setInstant(true);
      setIndex(index - total);
    } else if (index < total) {
      setInstant(true);
      setIndex(index + total);
    }
  };
  return <section className="py-20 bg-carbon border-b border-carbon-line overflow-hidden" aria-roledescription="carousel" aria-label="Preview of the coaching pages included" data-id="element-63" data-mp="15zmqvqred3mv">
      <div className="max-w-7xl mx-auto px-4" data-id="element-64" data-mp="dbz8midlmp3e">
        <div className="text-center mb-12" data-id="element-65" data-mp="rz24ylq6qrp5">
          <p className="inline-block text-gold font-bold tracking-widest uppercase text-xs sm:text-sm border border-gold/50 rounded-full px-4 py-1 mb-5" data-id="element-66" data-mp="s03pdgt3cqjj">
            A Look Inside the 6-in-1 System
          </p>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-67" data-mp="3vmklxytouje6">
            See what&apos;s inside{' '}
            <span className="text-gold" data-id="element-68" data-mp="k8bs1cldg458">Brazilian Jiu Jitsu Coach Ready</span>
          </h2>
          <p className="text-base md:text-lg text-mist max-w-2xl mx-auto" data-id="element-69" data-mp="o9nljt7d99ip">
            Real visual coaching pages designed to help you teach with more
            structure, clarity, and confidence.
          </p>
        </div>

        <div className="relative" data-id="element-70" data-mp="xzqe5sakslgi">
          <motion.div className="cursor-grab active:cursor-grabbing touch-pan-y" drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.18} dragSnapToOrigin onDragEnd={(_, info) => {
          if (info.offset.x < -50) go(1);else if (info.offset.x > 50) go(-1);
        }} data-id="element-71" data-mp="xyp15ia01ow5">
            <motion.div className="flex" animate={{
            x: `${offset}%`
          }} transition={instant || reduceMotion ? {
            duration: 0
          } : {
            duration: 0.3,
            ease
          }} onAnimationComplete={handleAnimationComplete} data-id="element-72" data-mp="f9s97l22r8h3">
              {extendedSlides.map((slide, slideIndex) => {
              const isActive = slideIndex === index;
              return <div key={`${slide.src}-${slideIndex}`} className="shrink-0 basis-full md:basis-1/3 px-2 sm:px-6 md:px-3" aria-hidden={!isActive} data-id="element-73" data-mp="31gdi63wch9tm" data-mpi={slideIndex}>
                    <figure className={`h-full flex flex-col bg-carbon-graphite border rounded-2xl p-3 sm:p-4 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.9)] transition-[transform,opacity,border-color] duration-300 ${isActive ? 'border-gold opacity-100 md:scale-100' : 'border-gold/25 md:opacity-55 md:scale-[0.92]'}`} data-id="element-74" data-mp="2sfyu4wrzxgmf">
                      <div className="bg-carbon rounded-xl overflow-hidden flex items-center justify-center" data-id="element-75" data-mp="gns6y6zfz7el">
                        <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto object-contain select-none" data-id="element-76" data-mp="jrr8blgkmpld" />
                      </div>
                      <figcaption className="mt-3 flex items-center justify-center gap-2 text-xs sm:text-sm text-smoke uppercase tracking-wider" data-id="element-77" data-mp="9vxm1ad1vhqe">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold" aria-hidden="true" data-id="element-78" data-mp="10879k63xllz" />
                        {slide.label}
                      </figcaption>
                    </figure>
                  </div>;
            })}
            </motion.div>
          </motion.div>

          <button type="button" onClick={() => go(-1)} aria-label="Previous page" className="hidden md:flex absolute left-2 lg:-left-2 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-carbon border border-gold/60 text-gold hover:bg-gold hover:text-carbon transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-79" data-mp="3s8m30dn7t3od">
            <ChevronLeftIcon size={22} data-id="element-80" data-mp="2uo2p1i92fms7" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next page" className="hidden md:flex absolute right-2 lg:-right-2 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full bg-carbon border border-gold/60 text-gold hover:bg-gold hover:text-carbon transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-81" data-mp="38mtw4kpig86u">
            <ChevronRightIcon size={22} data-id="element-82" data-mp="3recxyo7stg7a" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-8" data-id="element-83" data-mp="3nb6wruzjmf7o">
          <button type="button" onClick={() => go(-1)} aria-label="Previous page" className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-gold/60 text-gold" data-id="element-84" data-mp="54922ind1mwu">
            <ChevronLeftIcon size={18} data-id="element-85" data-mp="2tvui1eg14lea" />
          </button>
          <div className="flex gap-2" data-id="element-86" data-mp="2y0kbkwn7tnw1">
            {carouselSlides.map((slide, dotIndex) => <button key={slide.src} type="button" onClick={() => goTo(dotIndex)} aria-label={`Go to ${slide.label}`} aria-current={dotIndex === activeReal} className={`h-2 rounded-full transition-[width,background-color] duration-200 ${dotIndex === activeReal ? 'w-8 bg-gold' : 'w-2 bg-carbon-line hover:bg-smoke'}`} data-id="element-87" data-mp="32xkh96g5komm" data-mpi={dotIndex} />)}
          </div>
          <button type="button" onClick={() => go(1)} aria-label="Next page" className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-gold/60 text-gold" data-id="element-88" data-mp="3qn52jvzs3xz2">
            <ChevronRightIcon size={18} data-id="element-89" data-mp="1wc03l4zn09q2" />
          </button>
        </div>

        <div className="text-center mt-12 max-w-3xl mx-auto" data-id="element-90" data-mp="1os8mniz0qads">
          <p className="text-xl md:text-2xl font-serif font-bold text-white mb-2" data-id="element-91" data-mp="1jzfndambju93">
            <span className="text-gold" data-id="element-92" data-mp="1f0l1u0wyvrla">300+ Visual Coaching Pages</span> Across
            6 Complete BJJ Volumes
          </p>
          <p className="text-base text-mist" data-id="element-93" data-mp="1a2t1cii9ax5h">
            Built to help you plan less, teach more, and give every class more
            structure.
          </p>
        </div>
      </div>
    </section>;
}